import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";
import { ItemsTable, size } from "./items";

/**
 * Importing from a USB drive or card (design §6.1.1). Shown only where the app
 * offers volumes: the desktop app. Volumes are read, never written.
 */
export function UsbImport({ libraryId }: { libraryId: string | null }) {
  const qc = useQueryClient();
  const volumes = useQuery({
    queryKey: ["volumes"],
    queryFn: () => unwrap(api.GET("/api/volumes")),
    refetchInterval: 3000,
    retry: false,
  });
  const [chosen, setChosen] = useState<string | null>(null);
  const [folders, setFolders] = useState<string[]>([]);
  const [batch, setBatch] = useState<string | null>(null);

  const volume = volumes.data?.find((v) => v.id === chosen) ?? null;
  const top = useQuery({
    queryKey: ["volumes", chosen, "folders"],
    enabled: chosen !== null,
    queryFn: () =>
      unwrap(
        api.GET("/api/volumes/{id}/folders", {
          params: { path: { id: chosen ?? "" }, query: {} },
        }),
      ),
  });
  const start = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/volumes/{id}/import", {
          params: { path: { id: chosen ?? "" } },
          body: { library_id: libraryId ?? "", folders },
        }),
      ),
    onSuccess: (p) => setBatch(p.batch_id),
  });
  const progress = useQuery({
    queryKey: ["volume-imports", batch],
    enabled: batch !== null,
    queryFn: () =>
      unwrap(
        api.GET("/api/volume-imports/{id}", {
          params: { path: { id: batch ?? "" } },
        }),
      ),
    refetchInterval: (q) => (q.state.data?.finished ? false : 1000),
  });
  const finished = progress.data?.finished ?? false;
  const items = useQuery({
    queryKey: ["imports", batch, "items"],
    enabled: batch !== null && finished,
    queryFn: () =>
      unwrap(
        api.GET("/api/imports/{id}/items", {
          params: { path: { id: batch ?? "" } },
        }),
      ),
  });

  // No volumes here (not the desktop app) or not allowed: nothing to show.
  if (volumes.isError) return null;
  const running = batch !== null && !finished;

  const toggle = (f: string) =>
    setFolders((current) =>
      current.includes(f) ? current.filter((x) => x !== f) : [...current, f],
    );
  const reset = () => {
    setBatch(null);
    setFolders([]);
    void qc.invalidateQueries({ queryKey: ["imports"] });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6" component="h3">
          USB drive or card
        </Typography>
        {volumes.data?.length === 0 && (
          <Typography color="text.secondary">
            Insert a USB drive or memory card; it will appear here.
          </Typography>
        )}
        {!running && !finished && (
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
            {volumes.data?.map((v) => (
              <Button
                key={v.id}
                variant={v.id === chosen ? "contained" : "outlined"}
                onClick={() => {
                  setChosen(v.id);
                  setFolders([]);
                }}
              >
                {v.label}
              </Button>
            ))}
          </Stack>
        )}

        {volume && !running && !finished && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Files from {volume.label} get it as their unit. Choose folders, or
              none for the whole volume. It is only read, never changed.
            </Typography>
            {top.data?.length === 0 && (
              <Typography variant="body2">
                No folders: the whole volume is imported.
              </Typography>
            )}
            <Stack direction="row" sx={{ flexWrap: "wrap", columnGap: 2 }}>
              {top.data?.map((f) => (
                <FormControlLabel
                  key={f}
                  control={
                    <Checkbox
                      checked={folders.includes(f)}
                      onChange={() => toggle(f)}
                    />
                  }
                  label={f}
                />
              ))}
            </Stack>
            {start.isError && (
              <Alert severity="error">{start.error.message}</Alert>
            )}
            <Button
              variant="contained"
              sx={{ alignSelf: "flex-start" }}
              disabled={!libraryId || start.isPending}
              onClick={() => start.mutate()}
            >
              Import{" "}
              {folders.length
                ? `${folders.length} folder${folders.length === 1 ? "" : "s"}`
                : "whole volume"}
            </Button>
          </Stack>
        )}

        {batch && progress.data && (
          <Stack spacing={1}>
            {!finished && (
              <LinearProgress
                variant="determinate"
                value={
                  progress.data.bytes_total
                    ? (100 * progress.data.bytes_done) /
                      progress.data.bytes_total
                    : 100
                }
                aria-label="Copying from the volume"
              />
            )}
            <Typography variant="body2">
              {progress.data.files_done} of {progress.data.files_total} files ·{" "}
              {size(progress.data.bytes_done)} of{" "}
              {size(progress.data.bytes_total)}
              {progress.data.current && !finished
                ? ` · copying ${progress.data.current}`
                : ""}
            </Typography>
            {progress.data.error && (
              <Alert severity="error">{progress.data.error}</Alert>
            )}
          </Stack>
        )}

        {finished && items.data && (
          <Stack spacing={1}>
            <Alert severity="success">
              Import from {volume?.label ?? "the volume"} finished; a manifest
              of every file is kept with the evidence.
            </Alert>
            <ItemsTable items={items.data} />
            <Button sx={{ alignSelf: "flex-start" }} onClick={reset}>
              Import more
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
